import { BANK_FILE_STAGE_LABELS, BRANCHES_TERMINALES } from "@hakili/domain-types";
import { formatDate, formatMoney, t } from "@hakili/i18n";
import {
  BANK_BORDER,
  BANK_FS_BODY,
  BANK_FS_META,
  BANK_FS_READ,
  BANK_TEXT,
  BANK_TEXT_2,
  Callout,
} from "@hakili/ui";
import { AlertTriangle, Check, X } from "lucide-react";
import type { ReactElement } from "react";

import { D2Card, D2Kv, dayMonthTime } from "./d2-parts.js";
import { DASH, fill } from "./dossiers.js";
import type { RefusalRead } from "./revue.js";
import type { FileOfferRead } from "./validation.js";

/**
 * DESIGN SPRINT D-1026-2 (Lane E), D2 frames 50 and 51 (`stagePerdu`, `stageRefuse`): an ended file is **a callout and two cards**,
 * nothing else: « Dossier clos. » / « Dossier refusé. », « Le chemin parcouru », and the card of what the file had (the last offer, or
 * what Hakili had read). No control: no door reopens a closed file (D-1019-2, DX-011). Every line is the file's own read; a figure the
 * doors do not carry is a missing line, never a made-up one.
 */

/** The record's own label of the ending (`BRANCHES_TERMINALES`): a stage label lives in domain-types, never as a literal here (09 §9.2). */
const SANS_SUITE =
  BRANCHES_TERMINALES.find((one) => one.id === "sansSuite")?.label ?? t("bank.clos.sansMotif");

/** « 25 octobre »: a calendar day (`YYYY-MM-DD`) or an instant, without its year. */
const dayMonth = (value: string | null): string => {
  if (value === null) return DASH;
  const instant = new Date(/^\d{4}-\d{2}-\d{2}$/u.test(value) ? `${value}T12:00:00.000Z` : value);
  if (Number.isNaN(instant.getTime())) return DASH;
  return formatDate(instant).replace(/[\s\u00a0\u202f]\d{4}$/u, "");
};

interface ChainLine {
  readonly key: string;
  /** `d`: done (the ink node, a check); `x`: ended (the red node, a cross). */
  readonly state: "d" | "x";
  readonly words: string;
  readonly small: string;
}

/** D2 `.chain`: a 30 px node with a 2 px ring, the connector a 2 px line at the node's centre; the last line is the ending. */
function ChainCard(props: { readonly lines: readonly ChainLine[] }): ReactElement {
  return (
    <D2Card title={t("bank.clos.chemin")} testId="bank-clos-chemin">
      <ol>
        {props.lines.map((line, index) => (
          <li
            key={line.key}
            className="relative grid grid-cols-[34px_1fr] gap-x-3 py-2"
            data-testid={`bank-clos-ligne-${line.key}`}
            data-state={line.state}
          >
            {index === props.lines.length - 1 ? null : (
              <span
                className={`absolute -bottom-1.5 left-[14px] top-10 border-l-2 ${BANK_BORDER}`}
                aria-hidden="true"
              />
            )}
            <span
              className={`row-span-2 grid h-[30px] w-[30px] place-items-center rounded-full border-2 text-white ${
                line.state === "x"
                  ? "border-[color:var(--hk-bad)] bg-[color:var(--hk-bad)]"
                  : "border-[color:var(--hk-text)] bg-[color:var(--hk-text)]"
              }`}
            >
              {line.state === "x" ? (
                <X size={14} strokeWidth={2.6} aria-hidden="true" />
              ) : (
                <Check size={14} strokeWidth={2.6} aria-hidden="true" />
              )}
            </span>
            <p className={`${BANK_FS_READ} font-semibold ${BANK_TEXT}`} data-words="">
              {line.words}
            </p>
            <p className={`${BANK_FS_BODY} ${BANK_TEXT_2}`} data-small="">
              {line.small}
            </p>
          </li>
        ))}
      </ol>
    </D2Card>
  );
}

export interface LastOffer {
  readonly amountFinanced: number;
  readonly durationMonths: number;
  /** As the door sends it (« 10.7 »). */
  readonly nominalRatePct: string;
  readonly validUntil: string | null;
  readonly sentAt: string | null;
}

/** The last offer the bank sent, from the file's offer read; `null` when none stood (a study withdrawn before any offer). */
export function lastOfferFrom(read: FileOfferRead | null, transmittedAt: string | null): LastOffer | null {
  const offer = read?.offer ?? null;
  if (read === null || offer === null) return null;
  return {
    amountFinanced: read.facts.amountFinanced,
    durationMonths: offer.durationMonths,
    nominalRatePct: offer.nominalRatePct,
    validUntil: read.sent.validUntil,
    sentAt: transmittedAt ?? read.sent.transmittedAt,
  };
}

const rate = (value: string): string => fill("bank.briefing.pct", { n: value.replace(".", ",") });

/** The reason a file was lost, in D2's sentence (frame 50 draws the expiry; the other ends use the same layout, their own words). */
const lostWords = (
  lost: { readonly reason: string; readonly detail?: string | null | undefined } | null,
  offer: LastOffer | null,
): string => {
  if (lost === null) return t("bank.clos.sansMotif");
  if (lost.reason === "expired")
    return offer?.validUntil === null || offer === null
      ? t("bank.clos.expireeSansJour")
      : fill("bank.clos.expiree", { jour: dayMonth(offer.validUntil) });
  if (lost.reason === "client-declined") return t("bank.clos.declinee");
  if (lost.reason === "not-retained") return t("bank.clos.nonRetenue");
  if (lost.reason === "withdrawn")
    return typeof lost.detail === "string" ? lost.detail : t("bank.clos.retiree");
  return t("bank.clos.sansMotif");
};

/** D2 frame 50: the lost file. `offer` is the last offer the bank sent; `null` when the study ended before any (no card, no line). */
export function PerduView(props: {
  readonly lost: { readonly reason: string; readonly at: string; readonly detail?: string | null } | null;
  readonly receivedAt: string | null;
  readonly offer: LastOffer | null;
}): ReactElement {
  const reason = lostWords(props.lost, props.offer);
  const offer = props.offer;
  const lines: readonly ChainLine[] = [
    {
      key: "recu",
      state: "d",
      words: offer === null ? t("bank.parcours.recu") : t("bank.clos.recuRevue"),
      small: dayMonthTime(props.receivedAt),
    },
    ...(offer === null
      ? []
      : [
          {
            key: "offre",
            state: "d" as const,
            words: t("bank.parcours.offre"),
            small: dayMonthTime(offer.sentAt),
          },
        ]),
    { key: "fin", state: "x", words: SANS_SUITE, small: reason },
  ];
  return (
    <div data-testid="bank-perdu" data-reason={props.lost?.reason ?? ""}>
      <Callout
        tone="warn"
        testId="bank-clos-callout"
        className="mb-4 w-full"
        icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
      >
        <p data-testid="bank-perdu-banniere">
          <span className="font-semibold">{t("bank.clos.titre")}</span> <span>{reason}.</span>
        </p>
        <p>{t("bank.clos.aucuneAction")}</p>
      </Callout>
      <div className="grid grid-cols-1 gap-x-4 min-[1100px]:grid-cols-2">
        <ChainCard lines={lines} />
        {offer === null ? null : (
          <D2Card title={t("bank.clos.derniereOffre")} testId="bank-clos-offre">
            <dl>
              <D2Kv label={t("bank.clos.montant")} value={formatMoney(offer.amountFinanced)} />
              <D2Kv
                label={t("bank.clos.duree")}
                value={fill("bank.clos.mois", { n: String(offer.durationMonths) })}
                plain
              />
              <D2Kv label={t("bank.clos.taux")} value={rate(offer.nominalRatePct)} />
              <D2Kv label={t("bank.clos.valable")} value={dayMonth(offer.validUntil)} plain />
            </dl>
          </D2Card>
        )}
      </div>
    </div>
  );
}

/** What Hakili had read, in D2's three rows; each `null` is a dash. */
export interface HakiliReading {
  readonly note: string | null;
  readonly part: string | null;
  /** « 48 mois (durée demandée) », as the briefing words it. */
  readonly term: string | null;
  readonly ltv: string | null;
}

/** D2 frame 51: the refused file. */
export function RefuseView(props: {
  readonly refusal: RefusalRead | null;
  readonly receivedAt: string | null;
  /** The review's handled-points count (`progress.handled`); `null` when the read carries none: the line is then not drawn. */
  readonly handled: number | null;
  readonly reading: HakiliReading;
}): ReactElement {
  const motive = props.refusal?.label ?? DASH;
  // D2 `.meta`: « {who} · {date} ». The refusal read says who, by kind (REFUSAL-BY); a refusal the console recorded has no author and no line.
  const by = props.refusal?.by ?? null;
  const who = by === null ? null : by.kind === "comite" ? t("bank.clos.comite") : by.name;
  const lines: readonly ChainLine[] = [
    { key: "recu", state: "d", words: t("bank.parcours.recu"), small: dayMonthTime(props.receivedAt) },
    ...(props.handled === null
      ? []
      : [
          {
            key: "revue",
            state: "d" as const,
            words: t("bank.clos.revueEnCours"),
            small: fill(props.handled <= 1 ? "bank.clos.pointUn" : "bank.clos.pointsN", {
              n: String(props.handled),
            }),
          },
        ]),
    { key: "fin", state: "x", words: BANK_FILE_STAGE_LABELS.refuse, small: motive },
  ];
  return (
    <div data-testid="bank-refuse" data-reason="refuse">
      <Callout
        tone="bad"
        testId="bank-clos-callout"
        className="mb-4 w-full"
        icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
      >
        <p data-testid="bank-refuse-banniere">
          <span className="font-semibold">{t("bank.refus.banniereTitre")}</span>{" "}
          <span className="font-normal">{fill("bank.refus.banniere", { motif: motive })}</span>
        </p>
        {who === null ? null : (
          <p className={`${BANK_FS_META} ${BANK_TEXT_2}`} data-meta="" data-testid="bank-refuse-meta">
            {`${who} · ${dayMonthTime(props.refusal?.at ?? null)}`}
          </p>
        )}
      </Callout>
      <div className="grid grid-cols-1 gap-x-4 min-[1100px]:grid-cols-2">
        <ChainCard lines={lines} />
        <D2Card title={t("bank.clos.luHakili")} testId="bank-clos-lu">
          <dl>
            <D2Kv label={t("bank.clos.noteSolidite")} value={props.reading.note ?? DASH} plain />
            <D2Kv
              label={
                props.reading.term === null
                  ? t("bank.clos.partQuotiteSans")
                  : fill("bank.clos.partQuotite", { duree: props.reading.term })
              }
              value={props.reading.part ?? DASH}
              plain
            />
            <D2Kv label={t("bank.clos.ltv")} value={props.reading.ltv ?? DASH} plain />
          </dl>
        </D2Card>
      </div>
    </div>
  );
}
